import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useMemo } from "react";
import CollapsibleFormElement from "../../common/collapsible-form-element";

export type LaraMemory = {
  id: string;
  name: string;
};

export default function LaraMemories({
  availableMemories,
  selectedMemories,
  setSelectedMemories,
  isLoading = false,
}: {
  availableMemories: LaraMemory[];
  selectedMemories: string[];
  setSelectedMemories: (ids: string[]) => void;
  isLoading?: boolean;
}) {
  const memoryStates = useMemo(() => {
    const states: { [key: string]: boolean } = {};
    availableMemories.forEach((memory) => {
      states[memory.id] = selectedMemories.includes(memory.id);
    });
    return states;
  }, [selectedMemories, availableMemories]);

  const handleChange =
    (memoryId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedMemories([...selectedMemories, memoryId]);
      } else {
        setSelectedMemories(selectedMemories.filter((id) => id !== memoryId));
      }
    };

  const handleSelectAll = () => {
    const allSelected = availableMemories.every(
      (memory) => memoryStates[memory.id]
    );

    if (allSelected) {
      setSelectedMemories([]);
    } else {
      setSelectedMemories(availableMemories.map((m) => m.id));
    }
  };

  return (
    <CollapsibleFormElement title="AI Translation Memories">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block" }}
      >
        Select translation memories to adapt the AI translations. Memories help
        maintain consistency with previous translations.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : availableMemories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No memories available.
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      availableMemories.length > 0 &&
                      availableMemories.every(
                        (memory) => memoryStates[memory.id]
                      )
                    }
                    indeterminate={
                      availableMemories.some(
                        (memory) => memoryStates[memory.id]
                      ) &&
                      !availableMemories.every(
                        (memory) => memoryStates[memory.id]
                      )
                    }
                    onChange={handleSelectAll}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="bold">
                    Select all memories
                  </Typography>
                }
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {availableMemories.map((memory) => (
                <Box
                  key={memory.id}
                  sx={{
                    flexBasis: {
                      xs: "100%",
                      sm: "calc(50%)",
                      md: "calc(50%)",
                    },
                    flexGrow: 0,
                    flexShrink: 0,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!memoryStates[memory.id]}
                        onChange={handleChange(memory.id)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" noWrap>
                        {memory.name}
                      </Typography>
                    }
                    sx={{
                      marginRight: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "calc(100% - 42px)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </>
        )}
      </Paper>
    </CollapsibleFormElement>
  );
}
